/**
 * The theme rail — **client review R3**, rebuilt for **client specification S3**.
 *
 * R3 moved the eight domain chips out of the Indicators pane into a slim vertical strip
 * left of the panel, labelled, filtering both panes from one control. S3 keeps the strip
 * and changes two things about it:
 *
 * 1. **The order is the server's.** `/meta/config.theme_defaults.order` is the client's own
 *    rail order — All, the eight §C1 domains, and **Indicators last, after Digital
 *    Agriculture**, which is the position the specification names in words. It is read, not
 *    restated: a reordered seed or a client revision cannot silently move the icon here.
 * 2. **A chip now selects a theme's default dataset**, not just a list filter. Until S3 the
 *    rail explicitly never touched the map (§F5.2, §F5.11); S3's table is a table of
 *    *defaults*, so picking Water shows the water layers with their provisional note and
 *    picking Digital Agriculture shows the in-preparation state and no dataset. The
 *    departure from §F5.2 is recorded in docs/NOTES.md; the writes are `applyThemeDefault`'s,
 *    so the rail, the cold-boot view and any later caller move the map the same way.
 *
 * ## The baseline band (client directive, 8 Sep 2026)
 *
 * > *"Since the baseline also came with themes/domains, we need to add baseline theme/domain
 * > filter buttons to the rail we have, but we should be able to distinguish that from the
 * > original domain filters and the indicators button as well."*
 *
 * So the rail has **three kinds of entry**, and they look like three kinds: the eight domains
 * keep their filled colour tiles and icon glyphs; the ten 2025 workbook sheets are **outlined
 * ochre tiles with a two-letter monogram** (`BaselineChip`); the Indicators icon keeps its own
 * rule and its brand-green tile. The group sits **between Digital Agriculture and the icon**,
 * which is the one place S3's "the icon sits after Digital Agriculture" moves — the icon is
 * still last, and the departure is in the S3 spec's addendum.
 *
 * The **position is still the server's**: the baseline keys are in `theme_defaults.order`
 * like every other theme, and `theme_defaults.baseline_themes` carries the two names a chip
 * needs. Nothing here knows a sheet exists, so a re-issued workbook changes the rail without
 * changing this file.
 *
 * ## The ten sheets fold behind one button (client directive, 9 Sep 2026)
 *
 * > *"the ten baseline chips make the rail too long — replace the band with ONE Baseline
 * > button."*
 *
 * They did: All + eight domains + ten sheets + the icon is ~1000 px of chips, and the rail
 * answered by scrolling, which put the Indicators icon below the fold on a laptop. So the ten
 * are now an **accordion inside the rail**, opened by one outlined-ochre *Baseline 2025*
 * button that sits exactly where the band's caption used to.
 *
 * **An accordion rather than a flyout**, deliberately. A flyout beside a 72 px rail has to be
 * absolutely positioned over the panel it is next to; at the short viewports this directive is
 * about, that is a menu covering the very Analytics panel a reader is choosing a theme for,
 * and it needs dismiss-on-outside-click, focus capture and an escape key before it is usable
 * at all. The accordion is the rail doing what the rail already does — a vertical list that
 * scrolls — and it costs no new interaction to learn.
 *
 * Three things keep it from re-creating the problem it solves:
 *
 * * it is **collapsed by default**, so a cold rail is eleven entries rather than twenty;
 * * the expansion **scrolls inside itself** (`.rail-baseline-list`, capped at 46vh), so ten
 *   sheets cannot push the rail's own height past the viewport;
 * * the **Indicators zone is sticky to the bottom** of the rail, so the icon is on screen
 *   whatever is open above it. That is the directive's own clause, enforced in CSS rather
 *   than hoped for.
 *
 * The open state is remembered **per session** (`sessionStorage`), and a baseline theme that
 * is already selected — a permalink, a reload — forces it open, because a pressed chip inside
 * a closed accordion is a selection the reader cannot see.
 *
 * ## Three background zones (client directive, 9 Sep 2026)
 *
 * The rail's three families now sit on three tints: pale green behind the eight domains,
 * ochre behind the Baseline button and its expansion, a deeper brand green behind the
 * Indicators icon. The tints are the portal's own (`--zone-*` in `tokens.css`) and are
 * **additional** to the shape and colour distinctions above, never a replacement for them:
 * everything a reader could tell apart before is still told apart the same way, which is what
 * keeps the rail legible to somebody who cannot separate the two greens. The tooltips are
 * untouched, en and fr.
 *
 * ## The Indicators chip
 *
 * The client: *"the icon is not available to guests and is not visible as usable to them.
 * It is for programme personas only."* So for `role: 'public'` it renders **disabled**, with
 * a lock glyph in place of the domain dot and a neutral sentence on `title` — a statement of
 * what the icon is, not an invitation to sign in. `aria-disabled` and the real `disabled`
 * attribute both, so it is announced as unavailable and cannot be reached by keyboard or by
 * a programmatic click.
 *
 * It is **not hidden**, and that is deliberate: an icon that disappears reads as a portal
 * that lost a feature, and the client's own wording is "not available", not "not present".
 * Nothing behind it is served to a guest either way — §I1 gates the data on every request
 * and this is the §I2 mirror, exactly as the access matrix is.
 *
 * ## What is unchanged
 *
 * The chips are still labelled (C2: `short_name` under the icon), the full name is still the
 * accessible name and the tooltip, the rail is still a `role="toolbar"`, and clicking the
 * selected chip still clears back to All.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  fetchConfig,
  fetchIndicators,
  fetchLayers,
  queryKeys,
  type Domain,
} from '../../lib/api';
import { openAccess } from '../../lib/access';
import { Icon, ICONS, type IconName } from '../../lib/icons';
import { selectIndicatorMapped, selectLayersMapped, useAppStore } from '../../lib/state';
import { toast } from '../../lib/toast';
import { applyThemeDefault } from './applyTheme';
import { firstPaneClearance } from './paneSwitch';
import {
  ALL_THEME,
  baselineTheme,
  domFilterOf,
  INDICATORS_THEME,
  isBaselineTheme,
  monogramOf,
  themeAllowed,
  themeEntry,
  themeOf,
  themeOrder,
} from './themes';

function iconFor(name: string | undefined): IconName {
  return name && name in ICONS ? (name as IconName) : 'globe';
}

/**
 * Where the baseline accordion's open state lives — **per session**, which is what the
 * client asked for.
 *
 * `sessionStorage` rather than the store or `localStorage`: the store is permalinked and a
 * folded rail is not a view worth sharing, while `localStorage` would make one afternoon's
 * exploring the default rail forever. A tab's lifetime is exactly the span "remembers open
 * state per session" names.
 *
 * Both accessors are wrapped: a browser set to block site data throws on the *read*, not only
 * on the write, and a rail that cannot render because storage is off is a worse bug than a
 * rail that forgets.
 */
const RAIL_BASELINE_KEY = 'svg.rail.baselineOpen';

function readBaselineOpen(): boolean {
  try {
    return sessionStorage.getItem(RAIL_BASELINE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeBaselineOpen(open: boolean): void {
  try {
    sessionStorage.setItem(RAIL_BASELINE_KEY, open ? '1' : '0');
  } catch {
    // Private mode, or site data blocked. The rail still works; it just forgets.
  }
}

interface RailChipProps {
  /** The theme key this chip selects — `all`, a §C1 domain id, or `indicators`. */
  theme: string;
  /** The §C1 row behind the chip, or `null` for the two pseudo-themes. */
  domain: Domain | null;
  selected: boolean;
  /** Full name — hover text and accessible name. */
  label: string;
  /** C2: the word printed under the icon. `short_name`, or "All"/"Indicators". */
  short: string;
  icon: IconName;
  /** Flat colour of the icon tile; the two pseudo-themes have no domain palette entry. */
  color: string;
  /** S3: the chip is on the rail and cannot be used. Renders dead, with a lock. */
  locked?: boolean;
  onClick: () => void;
}

function RailChip({
  theme, domain, selected, label, short, icon, color, locked, onClick,
}: RailChipProps) {
  const isAll = theme === ALL_THEME;
  return (
    <button
      type="button"
      className={`dom-mini${isAll ? ' all' : ''}${selected ? ' sel' : ''}${locked ? ' locked' : ''}`}
      data-dom={isAll ? '' : theme}
      data-theme={theme}
      aria-pressed={locked ? undefined : selected}
      disabled={locked}
      aria-disabled={locked ? true : undefined}
      title={label}
      aria-label={label}
      onClick={locked ? undefined : onClick}
      style={
        selected && !locked
          ? {
              borderColor: domain ? domain.color : 'var(--brand)',
              background: domain ? `${domain.color}14` : 'var(--brand-tint)',
            }
          : undefined
      }
    >
      <span className="ci" aria-hidden="true" style={{ background: color }}>
        <Icon name={locked ? 'lock' : icon} size={14} />
      </span>
      {/*
        `aria-hidden`, because the button already has the *full* name as its accessible name
        and a screen reader announcing "Water. Water Resources & Hydrology" is reading the
        same control twice. The word is for eyes; the label is for everything else.
      */}
      <span className="dom-mini-label" aria-hidden="true">
        {short}
      </span>
    </button>
  );
}

interface BaselineChipProps {
  theme: string;
  selected: boolean;
  /** The sheet's full name — hover text and accessible name. */
  label: string;
  /** The chip word the server sent (`BASELINE_CHIP`). */
  short: string;
  /** The server's two-letter mark, or null — then it is derived from the word. */
  monogram: string | null;
  onClick: () => void;
}

/**
 * One 2025 baseline sheet's chip — **deliberately not a domain chip**.
 *
 * The client asked for a rail on which the baseline group is told apart from the eight
 * domain filters *and* from the Indicators icon. Three treatments, one per kind:
 *
 * | kind | mark | tile |
 * | --- | --- | --- |
 * | the eight §C1 domains | the domain's own icon glyph | **filled** with the domain colour |
 * | the ten baseline sheets | a **two-letter monogram** | **outlined** in ochre, no fill |
 * | the Indicators icon | the chart glyph | filled brand green, behind its own rule |
 *
 * Ochre (`--ochre`) is the portal's own second accent — §G2's domain palette does not use it,
 * so no baseline chip can be mistaken for a domain it happens to match. Outlined rather than
 * filled because the difference has to survive a reader who cannot tell the two greens apart:
 * a hollow tile and a solid tile differ in *shape*, not only in hue.
 *
 * The monogram is `aria-hidden` for the same reason the domain chips' short name is: the
 * button already announces the sheet's full name, and "AF. Agroforestry" is one control read
 * twice. `data-baseline` is the attribute a test asserts the distinction on, so the assertion
 * survives a restyle.
 */
function BaselineChip({
  theme, selected, label, short, monogram, onClick,
}: BaselineChipProps) {
  return (
    <button
      type="button"
      className={`dom-mini bl${selected ? ' sel' : ''}`}
      data-theme={theme}
      data-baseline=""
      aria-pressed={selected}
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      <span className="ci" aria-hidden="true">
        {monogram || monogramOf(short)}
      </span>
      <span className="dom-mini-label" aria-hidden="true">
        {short}
      </span>
    </button>
  );
}

/**
 * The one **Baseline 2025** button the ten sheets fold behind — client directive, 9 Sep 2026.
 *
 * It is the ochre family the chips are in, and it is deliberately **not** a `.dom-mini`: a
 * chip selects a theme and this selects nothing, so giving it the chips' class would put a
 * control that maps no data into every "what is on the rail" query the portal makes. Its own
 * class carries the same outlined-ochre treatment.
 *
 * `sel` when a sheet is selected and the accordion happens to be closed — which cannot
 * currently happen (a selected sheet forces it open) but is the honest state to draw if it
 * ever can, rather than a button that hides a live selection.
 */
interface BaselineToggleProps {
  open: boolean;
  /** A sheet under this button is the current theme. */
  selected: boolean;
  count: number;
  onClick: () => void;
}

function BaselineToggle({ open, selected, count, onClick }: BaselineToggleProps) {
  const { t } = useTranslation();
  const label = t(open ? 'domainRail.baselineHide' : 'domainRail.baselineShow', { count });
  return (
    <button
      type="button"
      id="baselineToggle"
      className={`rail-toggle${open ? ' open' : ''}${selected ? ' sel' : ''}`}
      // The attribute the band's caption used to carry, so "the rail names this group once"
      // is asserted on the control that now does the naming.
      data-railband="baseline"
      aria-expanded={open}
      aria-controls="baselineChips"
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      <span className="ci" aria-hidden="true">
        <Icon name="chev" size={13} />
      </span>
      <span className="dom-mini-label" aria-hidden="true">
        {t('domainRail.baselineBand')}
      </span>
    </button>
  );
}

export function DomainRail() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const domFilter = useAppStore((s) => s.domFilter);
  const setDomFilter = useAppStore((s) => s.setDomFilter);
  const theme = themeOf(domFilter);
  const [baselineOpen, setBaselineOpen] = useState(readBaselineOpen);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  const domainById = new Map((config?.domains ?? []).map((d) => [d.id, d]));
  /**
   * The server's order, falling back to the domain list while `/meta/config` is answering.
   *
   * The fallback is the pre-S3 rail exactly — All plus the eight domains — so a slow config
   * shows a usable rail rather than an empty strip, and the Indicators chip appears with
   * the table that governs it rather than before it.
   */
  const order = themeOrder(config);
  const keys = order.length
    ? order
    : [ALL_THEME, ...(config?.domains ?? []).map((d) => d.id)];
  /**
   * The server's order, split into the rail's three families — the 9 Sep 2026 zones.
   *
   * Walked rather than sliced, so **order inside a zone is still the server's** and a table
   * that one day interleaves the families does not silently reorder anything. A catalogue with
   * no baseline loaded simply has an empty middle zone, and neither the button nor its
   * expansion renders.
   */
  const zones: { domains: string[]; baseline: string[]; indicators: string[] } = {
    domains: [],
    baseline: [],
    indicators: [],
  };
  for (const key of keys) {
    if (isBaselineTheme(key)) zones.baseline.push(key);
    else if (key === INDICATORS_THEME) zones.indicators.push(key);
    else zones.domains.push(key);
  }
  /**
   * **Arriving at a sheet opens the fold**; staying on one does not keep it open.
   *
   * A pressed chip inside a closed accordion is a selection the reader cannot see, so the two
   * ways to arrive at one without touching the button — a permalink and a reload — open it.
   * The trigger is the *transition into* a baseline theme rather than the state of being on
   * one, which is the difference between opening the fold for the reader and refusing to let
   * them close it.
   */
  const baselineSelected = isBaselineTheme(theme);
  const wasBaseline = useRef(false);
  useEffect(() => {
    if (baselineSelected && !wasBaseline.current) {
      writeBaselineOpen(true);
      setBaselineOpen(true);
    }
    wasBaseline.current = baselineSelected;
  }, [baselineSelected]);
  const toggleBaseline = () => {
    const next = !baselineOpen;
    writeBaselineOpen(next);
    setBaselineOpen(next);
  };

  /**
   * Picking a chip: the E2 clearance where it still applies, then the theme's default.
   *
   * **Round 4 E2 survives as the Indicators boundary.** The two pane tabs are gone, but the
   * rule they carried is exactly the one the Indicators icon needs: a choropleth under an
   * opaque raster is a map the reader cannot see, so crossing between the icon and the
   * themes clears the other side and says so once per direction. Between two *themes* there
   * is nothing to explain — `applyThemeDefault` swaps one dataset for another and the panel
   * repaints, which is the message.
   */
  const select = (next: string) => {
    // §F5.2's "click-again clears", kept: the rail has no separate "off" and the selected
    // chip is the only control a reader who wants everything back can find.
    const target = next === theme ? ALL_THEME : next;
    const entering = target === INDICATORS_THEME;
    const leaving = theme === INDICATORS_THEME && !entering;
    if (entering || leaving) {
      const before = useAppStore.getState();
      const cleared = entering
        ? selectLayersMapped(before) && 'layers'
        : selectIndicatorMapped(before) && 'indicator';
      before.switchSidebarTab(entering ? 'inds' : 'layers');
      if (cleared && firstPaneClearance(cleared)) toast(t(`sidebar.${cleared}Cleared`));
    }
    setDomFilter(domFilterOf(target));
    applyThemeDefault(target, { config, layers, indicators });
  };

  /** One §C1 domain chip, or the All pseudo-theme's — the rail's first zone. */
  const domainChip = (key: string) => {
    if (key === ALL_THEME) {
      return (
        <RailChip
          key={key}
          theme={key}
          domain={null}
          selected={theme === ALL_THEME}
          label={t('domainRail.all')}
          short={t('domainRail.allShort')}
          icon="globe"
          color={theme === ALL_THEME ? 'var(--brand)' : '#8B958A'}
          onClick={() => select(ALL_THEME)}
        />
      );
    }
    const domain = domainById.get(key);
    if (!domain) return null;
    return (
      <RailChip
        key={key}
        theme={key}
        domain={domain}
        selected={theme === key}
        label={domain.name}
        // §D1's `short_name` is the column written for exactly this: a label that fits a
        // chip. Falling back to the full name keeps a domain legible if the API ever
        // serves an empty one, rather than printing a blank chip.
        short={domain.short_name || domain.name}
        icon={iconFor(domain.icon)}
        color={domain.color}
        onClick={() => select(key)}
      />
    );
  };

  return (
    <div id="domRail" role="toolbar" aria-label={t('domainRail.title')} aria-orientation="vertical">
      {/* Zone 1 — the eight §C1 domains and All, on the portal's pale green. */}
      <div className="rail-zone" data-railzone="domains">
        {zones.domains.map(domainChip)}
      </div>

      {/* Zone 2 — the 2025 baseline: one button, and the ten sheets behind it. Absent
          entirely for a catalogue whose workbook has not been loaded, rather than a button
          that opens onto nothing. */}
      {zones.baseline.length ? (
        <div className="rail-zone" data-railzone="baseline">
          <BaselineToggle
            open={baselineOpen}
            selected={baselineSelected}
            count={zones.baseline.length}
            onClick={toggleBaseline}
          />
          {baselineOpen ? (
            /* Its own scroller, capped in `base.css`: ten sheets must not be able to push
               the rail past the viewport, which is the whole point of folding them away. */
            <div className="rail-baseline-list" id="baselineChips">
              {zones.baseline.map((key) => {
                const row = baselineTheme(config, key);
                // A key the sibling list does not describe has no name to print, so it is
                // skipped rather than rendered as an unlabelled square.
                if (!row) return null;
                return (
                  <BaselineChip
                    key={key}
                    theme={key}
                    selected={theme === key}
                    label={row.label}
                    short={row.short}
                    monogram={row.monogram}
                    onClick={() => select(key)}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Zone 3 — the Indicators icon, on its own tint and **sticky to the foot of the
          rail**: the client's clause is that expanding the baseline must not push it off
          screen, and a sticky footer is that clause enforced rather than estimated. */}
      {zones.indicators.map((key) => {
        const entry = themeEntry(config, key);
        // `openAccess` is the 2026-09-08 directive: while the server's read surface is
        // open the icon is nobody's locked icon, guest included. The lock is the same
        // code either way — it is the flag that is false, not the state that is gone.
        const locked = !themeAllowed(entry, role, openAccess(config));
        return (
          <div key={key} className="rail-zone" data-railzone="indicators">
            <RailChip
              theme={key}
              domain={null}
              selected={theme === INDICATORS_THEME}
              // Locked: a neutral statement of what the icon is. Not "sign in to unlock" —
              // the client asked that it not be visible as usable, and a call to action is
              // an affordance.
              label={locked ? t('domainRail.indicatorsLocked') : t('domainRail.indicators')}
              short={t('domainRail.indicatorsShort')}
              icon="chart"
              color={locked ? '#8B958A' : 'var(--brand)'}
              locked={locked}
              onClick={() => select(INDICATORS_THEME)}
            />
          </div>
        );
      })}
    </div>
  );
}

export default DomainRail;
